import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import { formatSyncError } from "../DeviceSync/deviceSyncUtils";
import { IPagedAudits, IPagingOptions } from "./edgeAuditsApi";

export const DEFAULT_PAGE_SIZE = 25;
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export type PagedAuditsFetcher = (deviceId: string, options: IPagingOptions) => Promise<IPagedAudits>;

/**
 * Server-side paging for one device's audits (the DeviceHealth pattern). Nothing is
 * requested until the device is resolved; the fetcher must be module-level or
 * otherwise referentially stable, because the request runs from an effect.
 */
export function usePagedAudits(fetcher: PagedAuditsFetcher, deviceId: string, refreshKey: number = 0) {
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);

  // A slow earlier response must not overwrite a newer one.
  const requestIdRef = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) {
      return undefined;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    let cancelled = false;
    setLoading(true);

    fetcher(deviceId, { pageNum: page + 1, pageSize })
      .then((result) => {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }
        setRows(result?.audits || []);
        setTotalCount(result?.totalCount || 0);
      })
      .catch((error) => {
        if (cancelled || requestId !== requestIdRef.current) {
          return;
        }
        addMessage({ message: formatSyncError(error, t`Failed to load the audits.`) });
        setRows([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // refreshKey re-runs the same page on demand (the Refresh button)
  }, [fetcher, deviceId, page, pageSize, refreshKey, addMessage]);

  const handlePageChange = useCallback((_event: unknown, nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE);
    setPage(0);
  }, []);

  return { rows, totalCount, page, pageSize, loading, handlePageChange, handlePageSizeChange };
}
